import logger from '@/lib/logger';
import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  useTheme,
} from '@mui/material';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useHasAnyRole } from '@/store/useAuthStore';
import { MeasurementWizardHeader } from '@/features/measurement/components/MeasurementWizardHeader';
import { ResultsDisplay } from '@/features/measurement/ProgressResultsView/components/ResultsDisplay';
import { TestInfoPanel } from '@/features/measurement/ProgressResultsView/components/TestInfoPanel';
import { PatientPanel } from '@/features/measurement/ProgressResultsView/components/PatientPanel';
import { SurveyPanel } from '@/features/measurement/ProgressResultsView/components/SurveyPanel';
import { ReferenceLabelsPanel } from '@/features/measurement/ProgressResultsView/components/ReferenceLabelsPanel';
import { PrintableTestResults } from '@/features/measurement/ProgressResultsView/components/PrintableTestResults';
import { useClassificationUpdates } from '@/features/measurement/ProgressResultsView/hooks/useClassificationUpdates';
import { initializeMeasurementResults } from '@/features/measurement/hooks/useSpecimenMeasurement';
import { testService, type TestDetailResponse } from '@/services/api/testService';
import { usePatient } from '@/hooks/usePatients';
import '@/features/measurement/ProgressResultsView/print.css';

interface ResultsViewProps {
  testId: string;
  onClose: () => void;
}

function parseTestResults(test: TestDetailResponse) {
  return {
    completedAt: test.completedAt ? new Date(test.completedAt).getTime() : Date.now(),
    channels:
      test.pathogenClassificationResults?.map((result) => ({
        channel: result.channelId,
        pathogen: result.pathogenName || `Channel ${result.channelId}`,
        result: result.status || 'inconclusive',
        impedance: result.concentration || 0,
        confidence: result.confidence,
        phase: 0,
      })) || [],
  };
}

const COMPLETED_STATUSES = ['complete', 'failed', 'classification_failed'];
const PENDING_STATUSES = ['classifying', 'running', 'specimen_running'];

export function ResultsView({ testId, onClose }: ResultsViewProps) {
  const theme = useTheme();
  const selectedProfile = useMeasurementWizardStore((s) => s.selectedProfile);
  const selectedPatientId = useMeasurementWizardStore((s) => s.selectedPatientId);
  const setSelectedPatientId = useMeasurementWizardStore((s) => s.setSelectedPatientId);
  const setPatientPanelMode = useMeasurementWizardStore((s) => s.setPatientPanelMode);
  const isResearcher = useHasAnyRole('researcher', 'admin');

  const { data: patientData } = usePatient(selectedPatientId);

  const [loadedTest, setLoadedTest] = useState<TestDetailResponse | null>(null);
  const [loadingTest, setLoadingTest] = useState(true);
  const [measurementResults, setMeasurementResults] = useState<any>(null);
  const [blockingError, setBlockingError] = useState<string | null>(null);

  // Patient auto-save guards
  const isInitialLoadRef = useRef(true);
  const originalPatientIdRef = useRef<string | null | undefined>(undefined);
  const isUnmountingRef = useRef(false);

  // Load test on mount
  useEffect(() => {
    const loadTest = async () => {
      setLoadingTest(true);
      try {
        const test = await testService.getTestById(testId);
        setLoadedTest(test);

        if (test.patientId) {
          setSelectedPatientId(test.patientId);
          originalPatientIdRef.current = test.patientId;
          setPatientPanelMode('details');
        } else {
          setSelectedPatientId(null);
          originalPatientIdRef.current = null;
          setPatientPanelMode('anonymous');
        }

        const status = test.status?.toLowerCase() || '';

        if (COMPLETED_STATUSES.includes(status)) {
          setMeasurementResults(parseTestResults(test));
        } else if (PENDING_STATUSES.includes(status)) {
          setMeasurementResults(initializeMeasurementResults(selectedProfile));
        } else if (status === 'cancelled') {
          setBlockingError('This test was cancelled.');
        } else {
          setBlockingError('Test cannot be displayed.');
        }
      } catch (error) {
        logger.error('[ResultsView] Failed to load test:', error);
        setBlockingError('Failed to load test data. Please try again.');
      } finally {
        setLoadingTest(false);
        isInitialLoadRef.current = false;
      }
    };

    loadTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // Track unmount to prevent saves during cleanup
  useEffect(() => {
    isUnmountingRef.current = false;
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  // Auto-save patient association when it changes
  useEffect(() => {
    if (!testId || isInitialLoadRef.current || isUnmountingRef.current) return;
    if (selectedPatientId === originalPatientIdRef.current) return;
    if (selectedPatientId === null && originalPatientIdRef.current !== null) return;

    const updatePatient = async () => {
      try {
        await testService.updateTestPatient(testId, selectedPatientId);
        originalPatientIdRef.current = selectedPatientId;
      } catch (error) {
        logger.error('[ResultsView] Failed to update patient association:', error);
      }
    };

    updatePatient();
  }, [selectedPatientId, testId]);

  // Real-time classification updates via SignalR
  useClassificationUpdates(testId, 'completed', setMeasurementResults, setLoadedTest);

  return (
    <>
      {/* Hidden print-optimised layout */}
      {measurementResults && (
        <PrintableTestResults
          testId={testId}
          testInfo={{
            createdAt: loadedTest?.createdAt || new Date().toISOString(),
            operatorName: loadedTest?.operatorName || 'Unknown',
            deviceUid: loadedTest?.deviceUid || 'Unknown',
            status: loadedTest?.status || 'COMPLETE',
            sampleCode: loadedTest?.sampleCode || 'N/A',
          }}
          measurementResults={measurementResults}
          patient={patientData}
          notes={loadedTest?.notes}
          additionalInfo={loadedTest?.additionalInfo}
        />
      )}

      {/* Screen view */}
      <Box
        className="screen-view"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.default
              : 'linear-gradient(180deg, #E8F5FA 0%, #FFFFFF 100%)',
        }}
      >
        <MeasurementWizardHeader
          showCloseButton
          showSharePrint
          showDeviceStatus={false}
          onClose={onClose}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1600,
              px: { xs: 2, md: 4, lg: 6 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 4,
              }}
            >
              {/* Left panel: loading → results */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 2,
                }}
              >
                {loadingTest ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      p: 4,
                    }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading test data...
                    </Typography>
                  </Box>
                ) : (
                  <ResultsDisplay
                    measurementResults={measurementResults}
                    notes={loadedTest?.notes}
                    testId={testId}
                  />
                )}
              </Box>

              {/* Right panel: info + patient + survey + research */}
              <Box
                className="screen-only"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  alignItems: 'stretch',
                }}
              >
                {loadedTest && (
                  <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3 }}>
                    <TestInfoPanel
                      testInfo={{
                        createdAt: loadedTest.createdAt,
                        operatorName: loadedTest.operatorName,
                        deviceUid: loadedTest.deviceUid,
                        status: loadedTest.status,
                        sampleCode: loadedTest.sampleCode,
                      }}
                    />
                  </Box>
                )}

                <Box
                  className="patient-panel survey-panel"
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    flex: 1,
                  }}
                >
                  <PatientPanel />
                  <SurveyPanel
                    testId={testId}
                    initialSurveyData={useMemo(
                      () => loadedTest?.additionalInfo,
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                      [loadedTest?.additionalInfo],
                    )}
                  />
                  {isResearcher && (
                    <ReferenceLabelsPanel
                      testId={testId}
                      channels={
                        (selectedProfile as any)?.channels ||
                        loadedTest?.pathogenClassificationResults?.map((r: any) => ({
                          channelId: r.channelId,
                          pathogenName: r.pathogenName,
                        }))
                      }
                      mode="view"
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Blocking error dialog */}
        <Dialog
          open={!!blockingError}
          onClose={() => {}}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cannot Display Test</DialogTitle>
          <DialogContent>
            <Alert severity="error">{blockingError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
